import { Sparkles } from "lucide-react";
import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

type BundleCard = {
  count: number;
  names: string[];
  separateHref?: string;
};

type AiActionCard = {
  title: string;
  detail: string;
  label: string;
  href: string;
};

export type SupportTranslation = {
  from: string;
  originalText: string;
};

function safeHref(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  if (value.startsWith("/")) return value;
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? value : undefined;
  } catch {
    return undefined;
  }
}

export function readSupportPresentation(metadata: unknown): {
  bundle?: BundleCard;
  aiAction?: AiActionCard;
  translation?: SupportTranslation;
} {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata))
    return {};
  const root = metadata as Record<string, unknown>;
  const presentation =
    root.support_workspace &&
    typeof root.support_workspace === "object" &&
    !Array.isArray(root.support_workspace)
      ? (root.support_workspace as Record<string, unknown>)
      : root;
  const bundleRaw = presentation.bundle;
  const actionRaw = presentation.ai_action;
  const translationRaw = presentation.translation;
  const result: {
    bundle?: BundleCard;
    aiAction?: AiActionCard;
    translation?: SupportTranslation;
  } = {};

  if (bundleRaw && typeof bundleRaw === "object" && !Array.isArray(bundleRaw)) {
    const bundle = bundleRaw as Record<string, unknown>;
    const count = Number(bundle.count);
    const names = Array.isArray(bundle.names)
      ? bundle.names.filter((name): name is string => typeof name === "string")
      : [];
    if (Number.isFinite(count) && count > 1) {
      result.bundle = {
        count,
        names,
        separateHref: safeHref(bundle.separate_href),
      };
    }
  }

  if (actionRaw && typeof actionRaw === "object" && !Array.isArray(actionRaw)) {
    const action = actionRaw as Record<string, unknown>;
    const href = safeHref(action.href);
    if (
      href &&
      typeof action.title === "string" &&
      typeof action.detail === "string" &&
      typeof action.label === "string"
    ) {
      result.aiAction = {
        title: action.title,
        detail: action.detail,
        label: action.label,
        href,
      };
    }
  }

  if (
    translationRaw &&
    typeof translationRaw === "object" &&
    !Array.isArray(translationRaw)
  ) {
    const translation = translationRaw as Record<string, unknown>;
    if (
      typeof translation.from === "string" &&
      typeof translation.original_text === "string"
    ) {
      result.translation = {
        from: translation.from,
        originalText: translation.original_text,
      };
    }
  }
  return result;
}

export function SupportPresentationCards({
  metadata,
  section = "all",
}: {
  metadata: unknown;
  section?: "all" | "bundle" | "aiAction";
}) {
  const { t } = useLanguage();
  const { bundle, aiAction } = readSupportPresentation(metadata);
  const [actionDismissed, setActionDismissed] = useState(false);
  if (!bundle && !aiAction) return null;

  return (
    <div className="support-presentation-cards">
      {bundle && section !== "aiAction" && (
        <div className="support-bundle-card">
          <span>
            <strong>
              {t("support.bundle.count", "{count} reports bundled").replace(
                "{count}",
                String(bundle.count)
              )}
            </strong>
            {bundle.names.length > 0
              ? ` – ${t(
                  "support.bundle.detail",
                  "same issue in the same property. Also from {names}. Replies and work orders go to everyone."
                ).replace("{names}", bundle.names.join(", "))}`
              : ""}
          </span>
          {bundle.separateHref && (
            <a href={bundle.separateHref}>
              {t("support.bundle.separate", "Separate")}
            </a>
          )}
        </div>
      )}
      {aiAction && section !== "bundle" && !actionDismissed && (
        <div className="support-ai-action-card">
          <button
            type="button"
            className="support-ai-action-dismiss"
            onClick={() => setActionDismissed(true)}
          >
            {t("support.aiAction.dismiss", "Dismiss")}
          </button>
          <div className="flex items-center gap-2">
            <Sparkles size={13} />
            <strong>{aiAction.title}</strong>
          </div>
          <span>{aiAction.detail}</span>
          <a href={aiAction.href}>{aiAction.label}</a>
        </div>
      )}
    </div>
  );
}
